// src/pages/admin/AdminEvents.jsx
import { useEffect, useMemo, useState } from "react";
import TipTapEditor from "../../components/TipTap/TipTapEditor";
import FormBuilder, { emptyCustomForm } from "../../components/Form/FormBuilder";
import "./Admin.css";

const emptyEventTemplate = {
    id: null,
    slug: "",
    name: "",
    status: "draft",
    excerpt: "",
    tags: [],
    eventBody: "",
    eventImageUrl: "",
    baseStartUTC: "",
    timezone: "America/Toronto",
    endsUTC: "",
    durationMinutes: 60,
    host: "",
    eventLink: "",
    cost: {
        type: "free", // "free" | "paid" | "custom"
        amount: "",
        label: "",
    },
    recurrence: {
        type: "one-time", // "one-time" | "weekly" | "bi-weekly" | "every-x-days" | "monthly-nth"
        config: {
            interval: 1,
            weekdays: [],
            nths: [],
        },
        exclusions: [],
    },
    customForm: emptyCustomForm,
};


const weekdayOptions = [
    { label: "Mon", value: 1 },
    { label: "Tue", value: 2 },
    { label: "Wed", value: 3 },
    { label: "Thu", value: 4 },
    { label: "Fri", value: 5 },
    { label: "Sat", value: 6 },
    { label: "Sun", value: 7 },
];

const timezones = Intl.supportedValuesOf('timeZone');

// Helpers: UTC <-> datetime-local value
function toLocalInputValue(utcString) {
    if (!utcString) return "";
    const d = new Date(utcString);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function fromLocalInputValue(localString) {
    if (!localString) return "";
    const d = new Date(localString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
}

function getAnswerLabel(questions, key) {
    const list = Array.isArray(questions) ? questions : [];
    const match = list.find((question) => String(question?.id || "").trim() === String(key).trim());
    const label = String(match?.label || "").trim();
    return label || key;
}

function AdminEvents() {
    // --- data + loading state ---
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [eventsError, setEventsError] = useState(null);

    // --- UI state ---
    const [mode, setMode] = useState("list"); // "list" | "add" | "edit"
    const [search, setSearch] = useState("");
    const [form, setForm] = useState(emptyEventTemplate);
    const [editingId, setEditingId] = useState(null);
    const [newExclusionDate, setNewExclusionDate] = useState("");
    const [tagInput, setTagInput] = useState("");

    // --- upload/save state ---
    const [uploadingCover, setUploadingCover] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // --- delete state ---
    const [deleteTarget, setDeleteTarget] = useState(null); // event being deleted
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [registrations, setRegistrations] = useState([]);
    const [loadingRegistrations, setLoadingRegistrations] = useState(false);
    const [registrationsError, setRegistrationsError] = useState("");
    const [deletingRegistrationId, setDeletingRegistrationId] = useState("");
    const [registrationsModalEvent, setRegistrationsModalEvent] = useState(null);
    const [modalRegistrations, setModalRegistrations] = useState([]);
    const [loadingModalRegistrations, setLoadingModalRegistrations] = useState(false);
    const [modalRegistrationsError, setModalRegistrationsError] = useState("");
    // --- Email-attendees panel state ---
    const [emailSubject, setEmailSubject] = useState("");
    const [emailHtml, setEmailHtml] = useState("");
    const [emailFileName, setEmailFileName] = useState("");
    const [sendingTest, setSendingTest] = useState(false);
    const [sendingBlast, setSendingBlast] = useState(false);
    const [hasSentTest, setHasSentTest] = useState(false);
    const [emailFeedback, setEmailFeedback] = useState("");
    const [emailError, setEmailError] = useState("");

    // --- Load events from server on mount ---
    useEffect(() => {
        let cancelled = false;

        async function loadEvents() {
            try {
                const res = await fetch("/.netlify/functions/get-events", {
                    method: "GET",
                    credentials: "include",
                });

                const data = await res.json().catch(() => ({}));

                if (!res.ok || !data.ok) {
                    if (!cancelled) {
                        setEventsError(data.error || "Failed to load events");
                        setLoadingEvents(false);
                    }
                    return;
                }

                if (!cancelled) {
                    setEvents(data.events || []);
                    setLoadingEvents(false);
                }
            } catch (err) {
                console.error("loadEvents error:", err);
                if (!cancelled) {
                    setEventsError("Network error while loading events");
                    setLoadingEvents(false);
                }
            }
        }

        loadEvents();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Derived filtered list ---
    const filteredEvents = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return events;
        return events.filter((ev) =>
            [ev.name, ev.slug].some((field) =>
                field?.toLowerCase().includes(q)
            )
        );
    }, [events, search]);

    const currentQuestions = useMemo(
        () => (Array.isArray(form?.customForm?.questions) ? form.customForm.questions : []),
        [form]
    );

    // Confirmed, de-duplicated recipients for the email-attendees panel.
    const confirmedRecipientCount = useMemo(() => {
        const seen = new Set();
        for (const entry of modalRegistrations) {
            const status = String(entry.status || "confirmed").toLowerCase();
            if (status !== "confirmed") continue;
            const email = String(entry.email || "").trim().toLowerCase();
            if (!email || seen.has(email)) continue;
            seen.add(email);
        }
        return seen.size;
    }, [modalRegistrations]);

    // --- Basic form helpers ---
    function startAdd() {
        // fresh copy so nested structures (like customForm.questions) don't leak between events
        setForm({
            ...emptyEventTemplate,
            customForm: {
                ...emptyCustomForm,
                questions: [],
            },
        });
        setEditingId(null);
        setNewExclusionDate("");
        setSaveError(null);
        setTagInput("");
        setRegistrations([]);
        setRegistrationsError("");
        setLoadingRegistrations(false);
        setMode("add");
    }


    function startEdit(event) {
        setForm({
            ...event,
            id: event.id ?? null,
            tags: Array.isArray(event.tags) ? event.tags : [],
            cost: {
                type: event.cost?.type || "free",
                amount: event.cost?.amount ?? "",
                label: event.cost?.label ?? "",
            },
            recurrence: {
                type: event.recurrence?.type || "one-time",
                config: {
                    interval: event.recurrence?.config?.interval ?? 1,
                    weekdays: event.recurrence?.config?.weekdays || [],
                    nths: event.recurrence?.config?.nths || [],
                },
                exclusions: event.recurrence?.exclusions || [],
            },
            customForm: {
                ...emptyCustomForm,
                ...(event.customForm || {}),
                questions: Array.isArray(event.customForm?.questions)
                    ? event.customForm.questions
                    : [],
            },
        });
        setNewExclusionDate("");
        setEditingId(event.id);
        setSaveError(null);
        setTagInput("");
        setRegistrations([]);
        setRegistrationsError("");
        setMode("edit");
    }


    function handleCancel() {
        setMode("list");
        setEditingId(null);
        setForm(emptyEventTemplate);
        setNewExclusionDate("");
        setUploadingCover(false);
        setSaving(false);
        setSaveError(null);
        setTagInput("");
        setRegistrations([]);
        setRegistrationsError("");
    }

    useEffect(() => {
        let cancelled = false;
        const slug = form?.slug;

        async function loadRegistrations() {
            if (mode !== "edit" || !slug) {
                setRegistrations([]);
                setRegistrationsError("");
                setLoadingRegistrations(false);
                return;
            }

            setLoadingRegistrations(true);
            setRegistrationsError("");

            try {
                const res = await fetch(
                    `/.netlify/functions/get-event-registrations?slug=${encodeURIComponent(slug)}`,
                    {
                        method: "GET",
                        credentials: "include",
                    }
                );
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.ok) {
                    throw new Error(data.error || "Failed to load registrations");
                }

                if (!cancelled) {
                    setRegistrations(Array.isArray(data.registrations) ? data.registrations : []);
                }
            } catch (err) {
                if (!cancelled) {
                    setRegistrationsError(err.message || "Failed to load registrations");
                    setRegistrations([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingRegistrations(false);
                }
            }
        }

        loadRegistrations();
        return () => {
            cancelled = true;
        };
    }, [mode, form?.slug]);

    async function fetchRegistrationsForSlug(slug, hooks) {
        if (!slug) {
            hooks.setRows([]);
            hooks.setError("");
            hooks.setLoading(false);
            return;
        }

        hooks.setLoading(true);
        hooks.setError("");

        try {
            const res = await fetch(
                `/.netlify/functions/get-event-registrations?slug=${encodeURIComponent(slug)}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) {
                throw new Error(data.error || "Failed to load registrations");
            }
            hooks.setRows(Array.isArray(data.registrations) ? data.registrations : []);
        } catch (err) {
            hooks.setError(err.message || "Failed to load registrations");
            hooks.setRows([]);
        } finally {
            hooks.setLoading(false);
        }
    }

    function resetEmailPanel() {
        setEmailSubject("");
        setEmailHtml("");
        setEmailFileName("");
        setSendingTest(false);
        setSendingBlast(false);
        setHasSentTest(false);
        setEmailFeedback("");
        setEmailError("");
    }

    async function openRegistrationsModal(eventItem) {
        setRegistrationsModalEvent(eventItem);
        setModalRegistrations([]);
        setModalRegistrationsError("");
        resetEmailPanel();
        await fetchRegistrationsForSlug(eventItem?.slug, {
            setRows: setModalRegistrations,
            setError: setModalRegistrationsError,
            setLoading: setLoadingModalRegistrations,
        });
    }

    async function handleDeleteRegistration(registrationId) {
        if (!registrationId) return;

        const confirmed = window.confirm("Delete this registration?");
        if (!confirmed) return;

        setDeletingRegistrationId(registrationId);
        setRegistrationsError("");
        setModalRegistrationsError("");

        try {
            const res = await fetch("/.netlify/functions/delete-event-registration", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: registrationId }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data.ok) {
                throw new Error(data.error || "Failed to delete registration");
            }

            setRegistrations((prev) => prev.filter((entry) => entry.id !== registrationId));
            setModalRegistrations((prev) => prev.filter((entry) => entry.id !== registrationId));
        } catch (err) {
            const message = err.message || "Failed to delete registration";
            setRegistrationsError(message);
            setModalRegistrationsError(message);
        } finally {
            setDeletingRegistrationId("");
        }
    }

    function closeRegistrationsModal() {
        setRegistrationsModalEvent(null);
        setModalRegistrations([]);
        setModalRegistrationsError("");
        setLoadingModalRegistrations(false);
        resetEmailPanel();
    }

    // --- Email-attendees handlers ---
    function handleEmailFileUpload(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setEmailHtml(String(reader.result || ""));
            setEmailFileName(file.name);
            setHasSentTest(false);
            setEmailFeedback("");
            setEmailError("");
        };
        reader.onerror = () => setEmailError("Could not read that file.");
        reader.readAsText(file);
        // allow re-uploading the same filename later
        e.target.value = "";
    }

    function handleEmailHtmlChange(e) {
        setEmailHtml(e.target.value);
        setHasSentTest(false);
    }

    async function postEmailBlast(body) {
        const res = await fetch("/.netlify/functions/send-event-email", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
            throw new Error(data.error || "Send failed");
        }
        return data;
    }

    async function handleSendTest() {
        const slug = registrationsModalEvent?.slug;
        if (!slug) return;
        if (!emailSubject.trim()) {
            setEmailError("Add a subject line before sending a test.");
            return;
        }
        if (!emailHtml.trim()) {
            setEmailError("Add HTML content before sending a test.");
            return;
        }
        setSendingTest(true);
        setEmailError("");
        setEmailFeedback("");
        try {
            const data = await postEmailBlast({
                slug,
                subject: emailSubject,
                html: emailHtml,
                test: true,
            });
            setHasSentTest(true);
            setEmailFeedback(`Test sent to ${data.sentTo || "your admin email"}. Check it looks right before sending to attendees.`);
        } catch (err) {
            setEmailError(err.message || "Test send failed");
        } finally {
            setSendingTest(false);
        }
    }

    async function handleSendBlast() {
        const slug = registrationsModalEvent?.slug;
        if (!slug) return;
        const count = confirmedRecipientCount;
        const confirmed = window.confirm(
            `Send this email to ${count} confirmed registrant${count === 1 ? "" : "s"} of "${registrationsModalEvent.name}"?\n\nThis cannot be undone.`
        );
        if (!confirmed) return;

        setSendingBlast(true);
        setEmailError("");
        setEmailFeedback("");
        try {
            const data = await postEmailBlast({
                slug,
                subject: emailSubject,
                html: emailHtml,
            });
            const failedNote = data.failed
                ? ` ${data.failed} failed.`
                : "";
            setEmailFeedback(`Sent to ${data.sent} of ${data.total} registrant${data.total === 1 ? "" : "s"}.${failedNote}`);
            setHasSentTest(false);
        } catch (err) {
            setEmailError(err.message || "Send failed");
        } finally {
            setSendingBlast(false);
        }
    }

    function handleFieldChange(e) {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    // --- Cost handlers ---
    function handleCostTypeChange(e) {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            cost: {
                type: value,
                amount: value === "paid" ? prev.cost.amount : "",
                label: value === "custom" ? prev.cost.label : "",
            },
        }));
    }

    function handleCostAmountChange(e) {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            cost: {
                ...prev.cost,
                amount: value,
            },
        }));
    }

    function handleCostLabelChange(e) {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            cost: {
                ...prev.cost,
                label: value,
            },
        }));
    }

    // --- Recurrence handlers ---
    function handleRecurrenceTypeChange(e) {
        const value = e.target.value;
        setForm((prev) => {
            let interval = prev.recurrence.config.interval ?? 1;
            if (value === "bi-weekly") interval = 2;
            if (value === "weekly") interval = 1;

            return {
                ...prev,
                recurrence: {
                    ...prev.recurrence,
                    type: value,
                    config: {
                        ...prev.recurrence.config,
                        interval,
                    },
                },
            };
        });
    }

    function handleRecurrenceIntervalChange(e) {
        const value = Number(e.target.value) || 1;
        setForm((prev) => ({
            ...prev,
            recurrence: {
                ...prev.recurrence,
                config: {
                    ...prev.recurrence.config,
                    interval: value,
                },
            },
        }));
    }

    function handleRecurrenceWeekdayToggle(luxonWeekday) {
        setForm((prev) => {
            const current = prev.recurrence.config.weekdays || [];
            const exists = current.includes(luxonWeekday);
            const next = exists
                ? current.filter((d) => d !== luxonWeekday)
                : [...current, luxonWeekday];

            return {
                ...prev,
                recurrence: {
                    ...prev.recurrence,
                    config: {
                        ...prev.recurrence.config,
                        weekdays: next,
                    },
                },
            };
        });
    }

    function handleRecurrenceNthToggle(nth) {
        setForm((prev) => {
            const current = prev.recurrence.config.nths || [];
            const exists = current.includes(nth);
            const next = exists
                ? current.filter((n) => n !== nth)
                : [...current, nth];
            return {
                ...prev,
                recurrence: {
                    ...prev.recurrence,
                    config: {
                        ...prev.recurrence.config,
                        nths: next,
                    },
                },
            };
        });
    }

    // --- Date exclusions ---
    function handleAddExclusion() {
        const value = newExclusionDate.trim();
        if (!value) return;
        setForm((prev) => {
            const existing = prev.recurrence.exclusions || [];
            if (existing.includes(value)) return prev;
            return {
                ...prev,
                recurrence: {
                    ...prev.recurrence,
                    exclusions: [...existing, value],
                },
            };
        });
        setNewExclusionDate("");
    }

    function handleRemoveExclusion(dateStr) {
        setForm((prev) => ({
            ...prev,
            recurrence: {
                ...prev.recurrence,
                exclusions: (prev.recurrence.exclusions || []).filter(
                    (d) => d !== dateStr
                ),
            },
        }));
    }

    // --- TAGS helpers ---
    function addTagFromInput() {
        const raw = tagInput.trim();
        if (!raw) return;

        // Support typing "tag1, tag2"
        const pieces = raw
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);

        setForm((prev) => {
            const existing = prev.tags || [];
            const existingLower = existing.map((t) => t.toLowerCase());
            const toAdd = [];

            for (const piece of pieces) {
                if (!existingLower.includes(piece.toLowerCase())) {
                    toAdd.push(piece);
                    existingLower.push(piece.toLowerCase());
                }
            }

            if (toAdd.length === 0) return prev;

            return {
                ...prev,
                tags: [...existing, ...toAdd],
            };
        });

        setTagInput("");
    }

    function handleTagKeyDown(e) {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addTagFromInput();
            return;
        }

        // Backspace to remove last tag if input empty
        if (e.key === "Backspace" && !tagInput && (form.tags?.length || 0) > 0) {
            e.preventDefault();
            setForm((prev) => ({
                ...prev,
                tags: prev.tags.slice(0, -1),
            }));
        }
    }

    function handleTagBlur() {
        addTagFromInput();
    }

    function handleRemoveTag(tagToRemove) {
        setForm((prev) => ({
            ...prev,
            tags: (prev.tags || []).filter((t) => t !== tagToRemove),
        }));
    }

    // --- IMAGE UPLOAD HELPER (used by both cover + TipTap) ---
    async function uploadImage(file) {
        // Read file into base64
        const dataBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result; // "data:<mime>;base64,AAAA..."
                if (typeof result !== "string")
                    return reject(new Error("Unexpected result"));
                const [, base64Part] = result.split("base64,");
                if (!base64Part) return reject(new Error("Invalid data URL"));
                resolve(base64Part);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        const contentType = file.type || "application/octet-stream";

        const res = await fetch("/.netlify/functions/upload-image", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
                filename: file.name,
                contentType,
                dataBase64,
            }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            console.error("Upload HTTP error", res.status, data);
            throw new Error(data.error || `Upload failed (${res.status})`);
        }

        const url = data.url || data.publicUrl || data.href;

        if (!url) {
            console.error("Upload response missing URL", data);
            throw new Error("Upload failed: no URL in response");
        }

        return url;
    }

    // Cover Image: choose file, upload, set eventImageUrl
    async function handleCoverFileChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploadingCover(true);
            const url = await uploadImage(file);
            setForm((prev) => ({
                ...prev,
                eventImageUrl: url,
            }));
        } catch (err) {
            console.error("Cover upload failed:", err);
        } finally {
            setUploadingCover(false);
        }
    }

    // TipTap event body change
    function handleEventBodyChange(html) {
        setForm((prev) => ({
            ...prev,
            eventBody: html,
        }));
    }

    // TipTap image upload uses same uploadImage helper
    async function handleEditorImageUpload(file) {
        return uploadImage(file);
    }

    // --- SAVE EVENT to Netlify function + Mongo ---
    async function handleSubmit(e) {
        e.preventDefault();
        setSaveError(null);
        setSaving(true);

        const normalized = {
            ...form,
            id: form.id || editingId || null, // let server generate if null
            durationMinutes: Number(form.durationMinutes) || 0,
            cost: {
                type: form.cost.type,
                amount:
                    form.cost.type === "paid"
                        ? Number(form.cost.amount) || 0
                        : null,
                label:
                    form.cost.type === "custom"
                        ? String(form.cost.label || "").trim()
                        : "",
            },
            recurrence: {
                type: form.recurrence.type,
                config: {
                    ...form.recurrence.config,
                },
                exclusions: form.recurrence.exclusions || [],
            },
        };

        try {
            const res = await fetch("/.netlify/functions/save-event", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(normalized),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data.ok) {
                setSaveError((data.error + ": " + data.details) || "Failed to save event");
                setSaving(false);
                return;
            }

            const saved = data.event;

            setEvents((prev) => {
                const exists = prev.some((ev) => ev.id === saved.id);
                if (exists) {
                    return prev.map((ev) => (ev.id === saved.id ? saved : ev));
                }
                return [...prev, saved];
            });

            // Reset
            setMode("list");
            setEditingId(null);
            setForm(emptyEventTemplate);
            setNewExclusionDate("");
            setUploadingCover(false);
            setSaving(false);
            setTagInput("");
        } catch (err) {
            console.error("save event error:", err);
            setSaveError("Network error while saving event");
            setSaving(false);
        }
    }

    // --- DELETE EVENT (with confirmation modal) ---
    function openDeleteModal(ev) {
        setDeleteTarget(ev);
        setDeleteError(null);
    }

    function closeDeleteModal() {
        if (deleting) return; // avoid closing while in-flight
        setDeleteTarget(null);
        setDeleteError(null);
    }

    async function handleConfirmDelete() {
        if (!deleteTarget?.id) {
            setDeleteError("Missing event id");
            return;
        }

        setDeleting(true);
        setDeleteError(null);

        try {
            const res = await fetch("/.netlify/functions/delete-event", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ id: deleteTarget.id }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data.ok) {
                setDeleteError(data.error || "Failed to delete event");
                setDeleting(false);
                return;
            }

            // Remove from local state
            setEvents((prev) => prev.filter((ev) => ev.id !== deleteTarget.id));

            // If we were editing this event, reset back to list
            if (editingId === deleteTarget.id) {
                handleCancel();
            }

            setDeleting(false);
            setDeleteTarget(null);
        } catch (err) {
            console.error("delete event error:", err);
            setDeleteError("Network error while deleting event");
            setDeleting(false);
        }
    }

    // --- LOADING / ERROR STATES (list-level) ---
    if (loadingEvents && mode === "list") {
        return <p>Loading events...</p>;
    }

    if (eventsError && mode === "list") {
        return (
            <div className="admin-events">
                <h1>Events</h1>
                <p className="ae-error">{eventsError}</p>
            </div>
        );
    }

    // --- FORM MODE (add/edit) ---
    if (mode === "add" || mode === "edit") {
        const heading = mode === "add" ? "Add New Event" : "Edit Event";

        return (
            <div className="admin-events">
                <h1>{heading}</h1>
                <p className="ae-hint">
                    This is the Event Adder form. Event body uses TipTap, and images are
                    uploaded via Netlify.
                </p>

                <form onSubmit={handleSubmit} className="ae-form">
                    {/* Basic info */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Basic Info</h2>
                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Slug</label>
                                <input
                                    name="slug"
                                    type="text"
                                    value={form.slug}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>
                            <div className="ae-field">
                                <label>Name</label>
                                <input
                                    name="name"
                                    type="text"
                                    value={form.name}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Excerpt</label>
                                <input
                                    name="excerpt"
                                    type="text"
                                    value={form.excerpt}
                                    onChange={handleFieldChange}
                                    required
                                />
                            </div>

                            {/* Tags input */}
                            <div className="ae-field-tags">
                                <label>Tags</label>
                                <div
                                    className="ae-tags-container"
                                    onClick={() => {
                                        const input = document.getElementById("event-tags-input");
                                        if (input) input.focus();
                                    }}
                                >
                                    {form.tags?.map((tag) => (
                                        <span key={tag} className="ae-tag-chip">
                                            {tag}
                                            <button
                                                type="button"
                                                className="ae-button-chip-close"
                                                onClick={() => handleRemoveTag(tag)}
                                                aria-label={`Remove tag ${tag}`}
                                            >
                                                ✕
                                            </button>
                                        </span>
                                    ))}

                                    <input
                                        id="event-tags-input"
                                        className="ae-tag-input"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                        onBlur={handleTagBlur}
                                        placeholder={
                                            form.tags?.length ? "" : "Type and press Enter"
                                        }
                                    />
                                </div>
                                <p className="ae-hint">
                                    Type a tag and press <strong>Enter</strong> or <strong>,</strong> to add.
                                </p>
                            </div>
                        </div>
                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Status</label>
                                <select
                                    name="status"
                                    value={form.status}
                                    onChange={handleFieldChange}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                            <div className="ae-field">
                                <label>Host</label>
                                <input
                                    name="host"
                                    value={form.host}
                                    onChange={handleFieldChange}
                                    type="text"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Content: cover + TipTap */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Content</h2>

                        {/* Cover image */}
                        <div className="ae-field">
                            <label>Cover Image</label>
                            <div className="ae-cover-upload-row">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleCoverFileChange}
                                />
                                {uploadingCover && (
                                    <span className="ae-cover-upload-status">Uploading...</span>
                                )}
                            </div>
                            {form.eventImageUrl && (
                                <div className="ae-cover-preview">
                                    <img src={form.eventImageUrl} alt="Event cover" />
                                </div>
                            )}
                        </div>

                        {/* TipTap Editor */}
                        <div className="ae-field">
                            <label>Event Body</label>
                            <TipTapEditor
                                value={form.eventBody}
                                onChange={handleEventBodyChange}
                                onUploadImage={handleEditorImageUpload}
                            />
                        </div>
                    </section>

                    {/* Timing */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Timing</h2>
                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Base Start (local, stored as UTC)</label>
                                <input
                                    type="datetime-local"
                                    value={toLocalInputValue(form.baseStartUTC)}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            baseStartUTC: fromLocalInputValue(e.target.value),
                                        }))
                                    }
                                />
                            </div>
                            <div className="ae-field">
                                <label>Ends (local, stored as UTC)</label>
                                <input
                                    type="datetime-local"
                                    value={toLocalInputValue(form.endsUTC)}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            endsUTC: fromLocalInputValue(e.target.value),
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Timezone</label>
                                <select
                                    name="timezone"
                                    value={form.timezone}
                                    onChange={handleFieldChange}
                                >
                                    {/* Optional: blank "placeholder" option */}
                                    <option value="">Select a timezone</option>
                                    {timezones.map((tz) => (
                                        <option key={tz} value={tz}>
                                            {tz}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="ae-field">
                                <label>Duration (minutes)</label>
                                <input
                                    type="number"
                                    name="durationMinutes"
                                    value={form.durationMinutes}
                                    onChange={handleFieldChange}
                                    min={0}
                                    className="ae-input-inline"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Access & Cost */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Access & Cost</h2>
                        <div className="ae-field">
                            <label>Event Link</label>
                            <input
                                name="eventLink"
                                value={form.eventLink}
                                onChange={handleFieldChange}
                                type="text"
                            />
                        </div>

                        <div className="ae-row">
                            <div className="ae-field">
                                <label>Cost</label>
                                <div className="ae-cover-upload-row">
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            value="free"
                                            checked={form.cost.type === "free"}
                                            onChange={handleCostTypeChange}
                                        />
                                        Free
                                    </label>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            value="paid"
                                            checked={form.cost.type === "paid"}
                                            onChange={handleCostTypeChange}
                                        />
                                        $
                                    </label>
                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            value="custom"
                                            checked={form.cost.type === "custom"}
                                            onChange={handleCostTypeChange}
                                        />
                                        Custom text
                                    </label>
                                    {form.cost.type === "paid" && (
                                        <input
                                            type="number"
                                            min={0}
                                            step="1"
                                            className="ae-input-inline"
                                            value={form.cost.amount}
                                            onChange={handleCostAmountChange}
                                            placeholder="Amount"
                                        />
                                    )}
                                    {form.cost.type === "custom" && (
                                        <input
                                            type="text"
                                            value={form.cost.label}
                                            onChange={handleCostLabelChange}
                                            placeholder="e.g. By donation"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Registration Form */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Registration Form</h2>

                        <div className="ae-field">
                            <label className="ae-checkbox-inline">
                                <input
                                    type="checkbox"
                                    checked={!!form.customForm?.enabled}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        setForm((prev) => ({
                                            ...prev,
                                            customForm: {
                                                ...(prev.customForm || emptyCustomForm),
                                                enabled,
                                            },
                                        }));
                                    }}
                                />
                                Include a custom form for this event
                            </label>
                            <p className="ae-hint">
                                When enabled, attendees will see these questions when they sign up for
                                this event.
                            </p>
                        </div>

                        {form.customForm?.enabled && (
                            <FormBuilder
                                value={form.customForm}
                                onChange={(nextCustomForm) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        customForm: nextCustomForm,
                                    }))
                                }
                            />
                        )}
                    </section>

                    {/* Recurrence */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Recurrence</h2>

                        <div className="ae-field">
                            <label>Recurrence Type</label>
                            <select
                                value={form.recurrence.type}
                                onChange={handleRecurrenceTypeChange}
                            >
                                <option value="one-time">One-time</option>
                                <option value="weekly">Weekly</option>
                                <option value="bi-weekly">Bi-weekly</option>
                                <option value="every-x-days">Every X days</option>
                                <option value="monthly-nth">Monthly (nth weekday)</option>
                            </select>
                        </div>

                        {(form.recurrence.type === "weekly" ||
                            form.recurrence.type === "bi-weekly" ||
                            form.recurrence.type === "monthly-nth") && (
                                <div className="ae-field">
                                    <label>Weekdays</label>
                                    <div className="ae-weekday-pills">
                                        {weekdayOptions.map(({ label, value }) => {
                                            const checked = form.recurrence.config.weekdays?.includes(value);
                                            return (
                                                <button
                                                    type="button"
                                                    key={value}
                                                    onClick={() => handleRecurrenceWeekdayToggle(value)}
                                                    className={
                                                        "ae-button-pill" + (checked ? " ae-button-pill--active" : "")
                                                    }
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                        {form.recurrence.type === "every-x-days" && (
                            <div className="ae-field">
                                <label>Every how many days?</label>
                                <input
                                    type="number"
                                    min={1}
                                    value={form.recurrence.config.interval ?? 1}
                                    onChange={handleRecurrenceIntervalChange}
                                    className="ae-input-inline"
                                />
                            </div>
                        )}

                        {form.recurrence.type === "weekly" && (
                            <p className="ae-hint">
                                Runs every week on selected weekdays.
                            </p>
                        )}
                        {form.recurrence.type === "bi-weekly" && (
                            <p className="ae-hint">
                                Runs every 2 weeks on selected weekdays.
                            </p>
                        )}
                        {form.recurrence.type === "monthly-nth" && (
                            <>
                                <div className="ae-field">
                                    <label>Which occurrences in the month?</label>
                                    <div className="ae-nth-pills">
                                        {[1, 2, 3, 4, 5].map((nth) => {
                                            const checked =
                                                form.recurrence.config.nths?.includes(nth);
                                            const label =
                                                nth === 1
                                                    ? "1st"
                                                    : nth === 2
                                                        ? "2nd"
                                                        : nth === 3
                                                            ? "3rd"
                                                            : nth === 4
                                                                ? "4th"
                                                                : "5th";
                                            return (
                                                <button
                                                    type="button"
                                                    key={nth}
                                                    onClick={() => handleRecurrenceNthToggle(nth)}
                                                    className={
                                                        "ae-button-pill" +
                                                        (checked ? " ae-button-pill--active" : "")
                                                    }
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <p className="ae-hint">
                                    Example: 1st + 3rd Monday of every month →{" "}
                                    <code>
                                        recurrence: {"{"} type: "monthly-nth", config: {"{"}
                                        nths: [1, 3], weekdays: [1], interval: 1 {"}"} {"}"}
                                    </code>
                                </p>
                            </>
                        )}
                    </section>

                    {/* Date exclusions */}
                    <section className="ae-section">
                        <h2 className="ae-section-title">Date Exclusions</h2>
                        <p className="ae-hint">
                            These specific dates (local) will be excluded from the recurrence
                            pattern.
                        </p>
                        <div className="ae-exclusions-bar">
                            <input
                                type="date"
                                value={newExclusionDate}
                                onChange={(e) => setNewExclusionDate(e.target.value)}
                                className="ae-inline-date-input"
                            />
                            <button
                                type="button"
                                onClick={handleAddExclusion}
                                className="ae-button ae-button-pill"
                            >
                                Add Exclusion
                            </button>
                        </div>

                        {form.recurrence.exclusions?.length > 0 ? (
                            <div className="ae-exclusions-chips">
                                {form.recurrence.exclusions.map((dateStr) => (
                                    <span key={dateStr} className="ae-exclusion-chip">
                                        {dateStr}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveExclusion(dateStr)}
                                            className="ae-button-chip-close"
                                            aria-label={`Remove ${dateStr}`}
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="ae-hint" style={{ marginTop: 8 }}>
                                No exclusions added yet.
                            </p>
                        )}
                    </section>

                    {/* Actions */}
                    {mode === "edit" && (
                        <section className="ae-section">
                            <h2 className="ae-section-title">Registrations</h2>
                            {loadingRegistrations && <p className="ae-hint">Loading registrations...</p>}
                            {!loadingRegistrations && registrationsError && (
                                <p className="ae-error">{registrationsError}</p>
                            )}
                            {!loadingRegistrations && !registrationsError && registrations.length === 0 && (
                                <p className="ae-hint">No registrations yet for this event.</p>
                            )}
                            {!loadingRegistrations && registrations.length > 0 && (
                                <div className="ae-registrations-list">
                                    {registrations.map((entry) => (
                                        <article key={entry.id} className="ae-registration-card">
                                            <div className="ae-registration-main">
                                                <strong>{entry.name || "Unnamed attendee"}</strong>
                                                <span>{entry.email}</span>
                                                <small>
                                                    {entry.createdAt
                                                        ? new Date(entry.createdAt).toLocaleString()
                                                        : "Unknown time"}
                                                    {" • "}
                                                    {entry.status || "confirmed"}
                                                </small>
                                            </div>
                                            <div className="ae-registration-actions">
                                                <button
                                                    type="button"
                                                    className="ae-button ae-button-danger-soft"
                                                    onClick={() => handleDeleteRegistration(entry.id)}
                                                    disabled={deletingRegistrationId === entry.id}
                                                >
                                                    {deletingRegistrationId === entry.id ? "Deleting..." : "Delete"}
                                                </button>
                                            </div>
                                            <details className="ae-registration-answers">
                                                <summary>View answers</summary>
                                                {Object.keys(entry.formAnswers || {}).length === 0 ? (
                                                    <p className="ae-hint">No custom answers provided.</p>
                                                ) : (
                                                    <ul>
                                                        {Object.entries(entry.formAnswers).map(([key, value]) => (
                                                            <li key={`${entry.id}-${key}`}>
                                                                <strong>{getAnswerLabel(currentQuestions, key)}:</strong>{" "}
                                                                {Array.isArray(value) ? value.join(", ") : String(value)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </details>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {saveError && <p className="ae-error">{saveError}</p>}

                    <div className="ae-form-actions">
                        <button
                            type="submit"
                            className="ae-button ae-button-primary"
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save Event"}
                        </button>

                        <button
                            type="button"
                            onClick={handleCancel}
                            className="ae-button ae-button-outline"
                        >
                            Cancel
                        </button>
                    </div>
                </form>

                {/* Delete confirmation modal (also available in edit mode) */}
                {deleteTarget && (
                    <div className="ae-modal-overlay">
                        <div className="ae-modal-content">
                            <h2>Delete Event</h2>
                            <p>
                                Are you sure you want to permanently delete{" "}
                                <strong>{deleteTarget.name || deleteTarget.slug}</strong>?
                                <br />
                                This will also remove its uploaded images.
                            </p>
                            {deleteError && (
                                <p className="ae-modal-error">{deleteError}</p>
                            )}
                            <div className="ae-modal-actions">
                                <button
                                    type="button"
                                    onClick={closeDeleteModal}
                                    disabled={deleting}
                                    className="ae-button ae-button-outline"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmDelete}
                                    disabled={deleting}
                                    className="ae-button ae-button-danger"
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // --- LIST MODE ---
    return (
        <div className="admin-events">
            <h1>Events</h1>

            <div className="ae-toolbar">
                <input
                    className="ae-search-input"
                    placeholder="Search events by name or slug..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <button
                    onClick={startAdd}
                    type="button"
                    className="ae-button ae-button-primary"
                >
                    Add New Event
                </button>
            </div>

            {filteredEvents.length === 0 ? (
                <p className="ae-hint">No events match your search.</p>
            ) : (
                <div className="ae-events-list">
                    {filteredEvents.map((ev) => (
                        <div
                            key={ev.id || ev._id}
                            className="ae-event-card"
                        >
                            <div className="ae-event-card-main">
                                <div className="ae-event-card-header">
                                    <strong>{ev.name}</strong>
                                    <span
                                        className={
                                            "ae-status-pill " +
                                            (ev.status === "published"
                                                ? "ae-status-pill--published"
                                                : ev.status === "draft"
                                                    ? "ae-status-pill--draft"
                                                    : "ae-status-pill--archived")
                                        }
                                    >
                                        {ev.status}
                                    </span>
                                </div>
                                <div className="ae-event-card-meta">
                                    <code>{ev.slug}</code>
                                    {ev.recurrence?.type && <> • {ev.recurrence.type}</>}
                                    {ev.cost?.type === "paid" &&
                                        typeof ev.cost.amount === "number" && (
                                            <> • ${ev.cost.amount}</>
                                        )}
                                    {ev.cost?.type === "custom" &&
                                        String(ev.cost?.label || "").trim() && (
                                            <> • {ev.cost.label}</>
                                        )}
                                    {ev.cost?.type === "free" && <> • Free</>}
                                </div>
                            </div>

                            <div className="ae-event-card-actions">
                                <button
                                    type="button"
                                    onClick={() => openRegistrationsModal(ev)}
                                    className="ae-button ae-button-outline"
                                >
                                    Registrations
                                </button>
                                <button
                                    type="button"
                                    onClick={() => startEdit(ev)}
                                    className="ae-button ae-button-outline"
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openDeleteModal(ev)}
                                    className="ae-button ae-button-danger-soft"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete confirmation modal in list mode */}
            {deleteTarget && (
                <div className="ae-modal-overlay">
                    <div className="ae-modal-content">
                        <h2>Delete Event</h2>
                        <p>
                            Are you sure you want to permanently delete{" "}
                            <strong>{deleteTarget.name || deleteTarget.slug}</strong>?
                            <br />
                            This will also remove its uploaded images.
                        </p>
                        {deleteError && (
                            <p className="ae-modal-error">{deleteError}</p>
                        )}
                        <div className="ae-modal-actions">
                            <button
                                type="button"
                                onClick={closeDeleteModal}
                                disabled={deleting}
                                className="ae-button ae-button-outline"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDelete}
                                disabled={deleting}
                                className="ae-button ae-button-danger"
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {registrationsModalEvent && (
                <div className="ae-modal-overlay">
                    <div className="ae-modal-content ae-modal-content--wide">
                        <h2>Registrations: {registrationsModalEvent.name}</h2>
                        {loadingModalRegistrations && <p className="ae-hint">Loading registrations...</p>}
                        {!loadingModalRegistrations && modalRegistrationsError && (
                            <p className="ae-modal-error">{modalRegistrationsError}</p>
                        )}
                        {!loadingModalRegistrations && !modalRegistrationsError && modalRegistrations.length === 0 && (
                            <p className="ae-hint">No registrations yet for this event.</p>
                        )}
                        {!loadingModalRegistrations && modalRegistrations.length > 0 && (
                            <div className="ae-registrations-list">
                                {modalRegistrations.map((entry) => (
                                    <article key={entry.id} className="ae-registration-card">
                                        <div className="ae-registration-main">
                                            <strong>{entry.name || "Unnamed attendee"}</strong>
                                            <span>{entry.email}</span>
                                            <small>
                                                {entry.createdAt
                                                    ? new Date(entry.createdAt).toLocaleString()
                                                    : "Unknown time"}
                                                {" • "}
                                                {entry.status || "confirmed"}
                                            </small>
                                        </div>
                                        <div className="ae-registration-actions">
                                            <button
                                                type="button"
                                                className="ae-button ae-button-danger-soft"
                                                onClick={() => handleDeleteRegistration(entry.id)}
                                                disabled={deletingRegistrationId === entry.id}
                                            >
                                                {deletingRegistrationId === entry.id ? "Deleting..." : "Delete"}
                                            </button>
                                        </div>
                                        <details className="ae-registration-answers">
                                            <summary>View answers</summary>
                                            {Object.keys(entry.formAnswers || {}).length === 0 ? (
                                                <p className="ae-hint">No custom answers provided.</p>
                                            ) : (
                                                <ul>
                                                    {Object.entries(entry.formAnswers).map(([key, value]) => (
                                                        <li key={`${entry.id}-${key}`}>
                                                            <strong>{getAnswerLabel(currentQuestions, key)}:</strong>{" "}
                                                            {Array.isArray(value) ? value.join(", ") : String(value)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </details>
                                    </article>
                                ))}
                            </div>
                        )}

                        <div className="ae-email-panel">
                            <h3>Email attendees</h3>
                            <p className="ae-hint">
                                Upload or paste an HTML email and send it to the{" "}
                                <strong>{confirmedRecipientCount}</strong> confirmed
                                registrant{confirmedRecipientCount === 1 ? "" : "s"} of
                                this event. Use <code>{"{name}"}</code> or{" "}
                                <code>{"{eventName}"}</code> in your HTML to personalise
                                per recipient.
                            </p>

                            <label className="ae-field-label" htmlFor="ae-email-subject">
                                Subject
                            </label>
                            <input
                                id="ae-email-subject"
                                type="text"
                                className="ae-input"
                                value={emailSubject}
                                onChange={(e) => {
                                    setEmailSubject(e.target.value);
                                    setHasSentTest(false);
                                }}
                                placeholder="Email subject line"
                            />

                            <div className="ae-email-upload-row">
                                <label className="ae-button ae-button-outline ae-file-label">
                                    Upload .html file
                                    <input
                                        type="file"
                                        accept=".html,text/html"
                                        onChange={handleEmailFileUpload}
                                        hidden
                                    />
                                </label>
                                {emailFileName && (
                                    <span className="ae-hint">Loaded: {emailFileName}</span>
                                )}
                            </div>

                            <label className="ae-field-label" htmlFor="ae-email-html">
                                HTML content
                            </label>
                            <textarea
                                id="ae-email-html"
                                className="ae-input ae-email-textarea"
                                value={emailHtml}
                                onChange={handleEmailHtmlChange}
                                placeholder="Paste your HTML email here, or upload a file above."
                                rows={8}
                            />

                            {emailHtml.trim() && (
                                <details className="ae-email-preview" open>
                                    <summary>Preview</summary>
                                    <iframe
                                        title="Email preview"
                                        className="ae-email-iframe"
                                        sandbox=""
                                        srcDoc={emailHtml}
                                    />
                                </details>
                            )}

                            {emailError && <p className="ae-modal-error">{emailError}</p>}
                            {emailFeedback && <p className="ae-hint ae-email-feedback">{emailFeedback}</p>}

                            <div className="ae-email-actions">
                                <button
                                    type="button"
                                    className="ae-button ae-button-outline"
                                    onClick={handleSendTest}
                                    disabled={sendingTest || sendingBlast}
                                >
                                    {sendingTest ? "Sending test..." : "Send test to myself"}
                                </button>
                                <button
                                    type="button"
                                    className="ae-button"
                                    onClick={handleSendBlast}
                                    disabled={
                                        !hasSentTest ||
                                        sendingBlast ||
                                        sendingTest ||
                                        confirmedRecipientCount === 0
                                    }
                                    title={
                                        !hasSentTest
                                            ? "Send a test to yourself first"
                                            : undefined
                                    }
                                >
                                    {sendingBlast
                                        ? "Sending..."
                                        : `Send to ${confirmedRecipientCount} attendee${confirmedRecipientCount === 1 ? "" : "s"}`}
                                </button>
                            </div>
                            {!hasSentTest && (
                                <p className="ae-hint">
                                    Send a test to yourself first to enable the send button.
                                </p>
                            )}
                        </div>

                        <div className="ae-modal-actions">
                            <button
                                type="button"
                                onClick={closeRegistrationsModal}
                                className="ae-button ae-button-outline"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminEvents;
